import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useCreateFeedbackMutation,
  useUpdateFeedbackMutation,
} from "../../../redux/slices/feedbackApiSlice";
import { typesMap } from "../helper";

const FeedbackForm = ({
  feedbackData,
  setFeedbackData,
  btnText,
  setFeedbacks = null,
  setFeedback = null,
  category = null,
  setIsEditing = null,
}) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.auth.userInfo);
  const isAdmin = userInfo?.role === "admin";

  const [createFeedback] = useCreateFeedbackMutation();
  const [updateFeedback] = useUpdateFeedbackMutation();

  const handleCreate = async () => {
    if (!feedbackData.title || !feedbackData.text) return;

    try {
      await createFeedback({
        title: feedbackData.title,
        text: feedbackData.text,
        type: feedbackData.type,
        category: category,
        is_internal: feedbackData.is_internal ?? false,
        status: feedbackData.status ?? "suggested",
      }).unwrap();

      toast.success("Votre feedback a bien été publié !");
      setFeedbackData({
        type: "",
        title: "",
        text: "",
        is_internal: false,
        status: "suggested",
      });
    } catch (error) {
      console.error("Error creating feedback:", error);
    }
  };

  const handleUpdate = async () => {
    if (!feedbackData.id || !feedbackData.title || !feedbackData.text) return;

    try {
      const dataToUpdate = {
        title: feedbackData.title,
        text: feedbackData.text,
      };

      if (isAdmin) {
        dataToUpdate.is_internal = feedbackData.is_internal;
        dataToUpdate.status = feedbackData.status;
      }

      const updatedFeedback = await updateFeedback({
        feedbackId: feedbackData.id,
        data: dataToUpdate,
      }).unwrap();
      if (updatedFeedback) {
        setFeedback((prevFeedback) => ({
          ...prevFeedback,
          ...updatedFeedback,
        }));
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Error updating feedback:", error);
    }
  };

  const textareaRef = useRef(null);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // Reset height
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Adjust height
    }
  }, [feedbackData.text]);

  return (
    <div className={`bg-white rounded-lg p-4  ${category && "mb-4 shadow"}`}>
      <select
        className="w-full p-2 border rounded mb-2 bg-white disabled:bg-black/10"
        value={feedbackData.type}
        onChange={(e) =>
          setFeedbackData((prev) => ({ ...prev, type: e.target.value }))
        }
        disabled={category === null}
      >
        <option value="">{t("feedback-type", "Type de feedback")}</option>
        {Object.entries(typesMap).map(([type, { formText }]) => (
          <option value={type}>{formText}</option>
        ))}
      </select>

      <input
        type="text"
        placeholder="Titre du feedback"
        className="w-full p-2 border rounded mb-2 text-lg font-semibold disabled:bg-black/10"
        value={feedbackData.title}
        onChange={(e) =>
          setFeedbackData((prev) => ({ ...prev, title: e.target.value }))
        }
        disabled={!feedbackData.type}
      />

      <textarea
        ref={textareaRef}
        placeholder="Détails sur le feedback"
        className="w-full p-2 border rounded mb-2 resize-none overflow-hidden min-h-28 disabled:bg-black/10"
        value={feedbackData.text}
        onChange={(e) =>
          setFeedbackData((prev) => ({ ...prev, text: e.target.value }))
        }
        onInput={(e) => {
          e.currentTarget.style.height = "auto";
          e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
        }}
        disabled={!feedbackData.type}
      />

      {isAdmin && (
        <div className="grid grid-cols-2 gap-2 mb-2">
          <select
            className="w-full p-2 border rounded bg-white"
            value={feedbackData.status}
            onChange={(e) =>
              setFeedbackData((prev) => ({ ...prev, status: e.target.value }))
            }
          >
            <option value="suggested">Suggéré</option>
            <option value="planned">Planifié</option>
            <option value="in progress">En cours</option>
            <option value="achieved">Réalisé</option>
          </select>
          <select
            className="w-full p-2 border rounded bg-white"
            value={feedbackData.is_internal}
            onChange={(e) =>
              setFeedbackData((prev) => ({
                ...prev,
                is_internal: e.target.value === "true",
              }))
            }
          >
            <option value={false}>Public</option>
            <option value={true}>Interne</option>
          </select>
        </div>
      )}

      <button
        className="bg-blue-100 hover:bg-blue-200 text-white px-4 py-2 rounded"
        onClick={category ? handleCreate : handleUpdate}
      >
        {btnText}
      </button>
      {setIsEditing && (
        <button
          className="ml-2 bg-black/10 hover:bg-black/20 px-4 py-2 rounded"
          onClick={() => setIsEditing(false)}
        >
          Annuler
        </button>
      )}
    </div>
  );
};

export default FeedbackForm;
